/**
 * This module provides utilities for handling secret values during runtime execution
 *
 * Core Features:
 * - Injects a Proxy (`SECRETS`) into globalThis, dynamically
 *      generating secret placeholders for context when compiling test data (e.g., SECRETS.apiKey → "SECRETS.apiKey").
 * - Detects if a code block or object contains secret placeholders.
 * - Extracts all used secret keys from a code block or object to send to the server for specific decryption.
 * - Replaces secret placeholders with their corresponding decrypted values from a provided secrets record.
 *
 *   Supports for nested structures - test data values can be a complex object or an array, we want to allow nested structures.
 * - Mutates the provided test data directly (in-place modification) of run parameters.
 **/

export interface NewSecretEntryPayload {
    key: string;
    value: string;
    projectId: string;
    description: string;
}

export interface SecretsEntry extends NewSecretEntryPayload {
    _id: string;
    createdBy: string;
    created_at: string;
    updated_at: string;
}

export interface SecretsRecord {
    [secretKey: string]: string;
}

declare global {
    // eslint-disable-next-line no-var
    var SECRETS: SecretsRecord;
}

const key = 'SECRETS';

export const secretsUtils = {
    get secretsKey() {
        return key;
    },
    get proxySecrets() {
        return new Proxy({}, {
            get(_, secretKey: string) {
                return `${key}.${secretKey}`;
            },
        });
    },
    injectProxySecretsStoreToGlobal() {
        Object.assign(globalThis, { [key]: this.proxySecrets });
    },
    containsSecrets(code: object | string) {
        return typeof code === 'string' ? code.includes(key) : JSON.stringify(code).includes(key);
    },
    extractSecretKeysFromCode(code: object | string) {
        const secretKeys = new Set<string>();
        const stringifiedCode = typeof code === 'string' ? code : JSON.stringify(code);
        const matches = stringifiedCode.match(new RegExp(`${key}\\.\\w+`, 'g'));
        matches?.map((match) => {
            const secretName = match.split('.')[1];
            if (secretName) {
                secretKeys.add(secretName);
            }
        });
        return secretKeys;
    },
    replaceValuesWithDecryptedSecrets({ testData, secretsRecord }: { testData: Record<string, any>; secretsRecord: SecretsRecord }) {
        const secretsKey = this.secretsKey;
        const escapedSecretsKey = secretsKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const secretRegex = new RegExp(`^${escapedSecretsKey}\\.(\\w+)$`);

        const replaceTestDataRecursively = (testDataEntry: any) => {
            if (Array.isArray(testDataEntry)) {
                testDataEntry.forEach((val, index) => {
                    if (typeof val === 'string') {
                        const match = val.match(secretRegex);
                        if (match) {
                            const secretName = match[1];
                            testDataEntry[index] = secretsRecord[secretName] ?? val;
                        }
                    } else if (typeof val === 'object' && val !== null) {
                        replaceTestDataRecursively(val);
                    }
                });
            } else if (testDataEntry && typeof testDataEntry === 'object') {
                Object.entries(testDataEntry).forEach(([key, value]) => {
                    if (typeof value === 'string') {
                        const match = value.match(secretRegex);
                        if (match) {
                            const secretName = match[1];
                            testDataEntry[key] = secretsRecord[secretName] ?? value;
                        }
                    } else if (typeof value === 'object' && value !== null) {
                        replaceTestDataRecursively(value);
                    }
                });
            }
        };

        replaceTestDataRecursively(testData);
    },
};
